import { useCallback, useEffect, useId } from "react";

import { Button } from "@components/Button";
import { Image } from "@components/Image";
import { Typography } from "@components/Typography";

import { useToysStore } from "@storage/global/useToysStore";
import { useAcceptToysControlMutation } from "@storage/queries/toys";
import { useDeclineToysControlMutation } from "@storage/queries/toys/toysGate";
import { type TToysAvailabilityStorageValue, toysAvailabilityStorage } from "@storage/sessionStorage";

import { ICON_PATHS } from "@configs/icons";

import { useToysSession } from "./useToysSession";

const TOY_CONTROL_PROPOSAL_TIMEOUT = 60_000;

export const ToyControlProposalPopUp = () => {
	const { activePrivateShowId, isOpenControlProposalPopUp, setIsOpenControlProposalPopUp } = useToysStore();
	const titleId = useId();
	const descriptionId = useId();
	const { sessionToken } = useToysSession();
	const { mutate: acceptToysControl } = useAcceptToysControlMutation(sessionToken);
	const { mutate: declineToysControl } = useDeclineToysControlMutation(sessionToken);

	const handleClose = useCallback(() => {
		const toysAvailability = toysAvailabilityStorage.getItemAsJson<TToysAvailabilityStorageValue>();

		if (toysAvailability?.privateShowId === activePrivateShowId) {
			toysAvailabilityStorage.setItemAsJson<TToysAvailabilityStorageValue>({
				...toysAvailability,
				isProposalPending: false,
			});
		}

		setIsOpenControlProposalPopUp(false);
	}, [activePrivateShowId, setIsOpenControlProposalPopUp]);

	const handleAccept = () => {
		if (!sessionToken) {
			return;
		}

		acceptToysControl();
		handleClose();
	};

	const handleDecline = useCallback(() => {
		if (sessionToken) {
			declineToysControl();
		}

		handleClose();
	}, [declineToysControl, handleClose, sessionToken]);

	useEffect(() => {
		if (!isOpenControlProposalPopUp) {
			return;
		}

		const timeoutId = window.setTimeout(handleDecline, TOY_CONTROL_PROPOSAL_TIMEOUT);

		return () => window.clearTimeout(timeoutId);
	}, [handleDecline, isOpenControlProposalPopUp]);

	if (!isOpenControlProposalPopUp) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-max flex items-center justify-center bg-black/84 p-4">
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				className="flex w-94 max-w-full flex-col gap-4 overflow-hidden rounded-2xl bg-block px-6 pt-5 pb-6"
			>
				<div className="flex h-8 items-center gap-3">
					<span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-toy">
						<Image width={20} height={20} src={ICON_PATHS.HANDY_24} alt="" aria-hidden="true" />
					</span>
					<Typography id={titleId} type="h2" size="base" weight="bold" className="tracking-[-0.3px]">
						The interactive toy control proposal
					</Typography>
				</div>

				<div className="flex h-17 items-center justify-center rounded-lg bg-black px-10">
					<Typography
						id={descriptionId}
						size="lg"
						align="center"
						className="max-w-62 text-[19px] leading-[normal] text-[#eed16b]"
					>
						The user wants to give you control of his sex toy
					</Typography>
				</div>

				<div className="flex h-10 gap-4">
					<Button
						type="toy"
						wide
						aria-label="Accept toy control"
						disabled={!sessionToken}
						onClick={handleAccept}
						className="h-10 px-4"
					>
						<Typography type="span" size="base" weight="bold" align="center">
							Accept
						</Typography>
					</Button>

					<Button
						type="secondary"
						wide
						aria-label="Decline toy control"
						disabled={!sessionToken}
						onClick={handleDecline}
						className="h-10 px-4"
					>
						<Typography type="span" size="base" weight="bold" align="center">
							Decline
						</Typography>
					</Button>
				</div>
			</div>
		</div>
	);
};
